import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { global } from "@shared/libs/Global";
import { callApi } from "@shared/api/restApi";
import SnGrid from "@shared/components/Grid/SnGrid";
import { ColumnConfig } from "@shared/components/Grid/SnGrid_Types";
import SnLabel from "@shared/components/SnLabel";
import SnTextBox from "@shared/components/SnTextBox";
import SnButton from "@shared/components/SnButton";
import { showDialog } from "@widgets/CommonDialog";
import {
  downloadErrorMessage,
  pickSaveTarget,
  writeBlob,
} from "@menu/SFA/SN_SFA_ISSUE_REG_002/SN_SFA_ISSUE_REG_002_DLL_BOX";

/**
 * 파일 첨부이력 팝업. 윈폼 M_SFA_ISS_REG_002_POP_09(File Info) 대응.
 * 본 DB(Postgres) sfa_issue_attach 를 파일명 검색어로 조회(executeScript/getDllList)하고
 * 선택한 행을 .NET DownloadFile(DLL/{폴더}) 중계로 내려받는다.
 */
interface Props {
  no_issue: string;
  /** 서버 DLL 폴더명 (getDllFolder 로 변환된 값) */
  dll_folder: string;
}

// SnGrid 는 columnConfig 가 없으면 그리드를 그리지 않는다(columnDefs 만으론 렌더링 안 됨). 메뉴 설정 없이 쓰는 팝업이라 직접 구성.
const makeColumn = (
  cd_column: string,
  nm_column: string,
  qt_width: number,
  no_seq: number
): ColumnConfig => ({
  cd_firm: "",
  cd_menu: "SN_SFA_ISSUE_REG_002",
  cd_grid: "grid_dll_his",
  cd_user: "",
  cd_column,
  nm_column,
  tp_column: "SnTextBox",
  fg_visible: "Y",
  no_seq,
  qt_width,
  yn_required: "N",
  yn_allowsorting: "Y",
  yn_fixed: "N",
  yn_sum: "N",
  yn_system: "N",
  qt_groupindex: -1,
  dc_editcon: "1 == 1", // 항상 읽기전용
});

const columnConfig: ColumnConfig[] = [
  makeColumn("nm_file", "FileName", 300, 1),
  makeColumn("tm_reg", "Upload Date", 160, 2),
  makeColumn("nm_user", "Uploader", 120, 3),
];

const SN_SFA_ISSUE_REG_002_POP_006 = ({ no_issue, dll_folder }: Props) => {
  const userInfo = global.getState().userInfo;
  const [rows, setRows] = useState<any[]>([]);
  const searchText = useRef<string>("");
  const focusedRow = useRef<any>(null);

  const onSearch = async () => {
    try {
      // tm_reg 는 서버에서 'YYYY-MM-DD HH24:MI:SS' 문자열로 내려온다
      const rtn = await callApi("executeScript/getDllList", {
        no_issue,
        search: searchText.current ?? "",
      });
      setRows(Array.isArray(rtn?.data) ? rtn.data : []);
      focusedRow.current = null;
    } catch (error) {
      console.error("파일 첨부이력 조회 실패:", error);
      setRows([]);
    }
  };

  useEffect(() => {
    onSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [no_issue]);

  // 윈폼 FN_Download 대응: 포커스 행의 파일을 DLL/{폴더} 에서 받는다. 저장 창은 서버 호출 전에 띄운다.
  const onDownload = async (row?: any) => {
    const target = row ?? focusedRow.current;
    if (!target?.nm_file) {
      await showDialog("alter", "내려받을 파일을 선택해 주세요.");
      return;
    }
    try {
      const save = await pickSaveTarget(target.nm_file);
      if (save === "cancel") return;

      const res = await axios.post(
        "/snotes/executeScript/downloadDllDotNet",
        { fg_item: dll_folder, nm_file: target.nm_file },
        {
          headers: {
            "X-TENANT-ID": userInfo.tenant_id || "DEFAULT",
          },
          responseType: "blob",
        }
      );
      await writeBlob(save, target.nm_file, new Blob([res.data]));
    } catch (err: any) {
      await showDialog(
        "error",
        downloadErrorMessage("파일 다운로드를 실패했습니다.", err)
      );
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        height: "100%",
        minHeight: 420,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <SnLabel text="Search" type="string" />
        <div style={{ width: 220 }}>
          <SnTextBox
            onValueChanged={(v) => (searchText.current = v)}
            onEnter={onSearch}
          />
        </div>
        <SnButton text="조회" onClick={onSearch} />
        <div style={{ flex: 1 }} />
        <SnButton text="다운로드" onClick={() => onDownload()} />
      </div>
      <div style={{ flex: 1, minHeight: 360 }}>
        <SnGrid
          seq={1}
          columnConfig={columnConfig}
          rowData={rows}
          isEditable={false}
          rowSelection="single"
          onFocusedRowChanged={(r) => (focusedRow.current = r)}
          onRowClicked={(e) => (focusedRow.current = e?.data ?? null)}
          onRowDoubleClicked={(e) => onDownload(e?.data)}
        />
      </div>
    </div>
  );
};

export default SN_SFA_ISSUE_REG_002_POP_006;
